// Operator brand surface — three tabs:
//   1. Inquiries  — the BD intake queue. Public /inquire form posts here.
//   2. Invites    — warm-lead invite links. Bypass the inquiry queue when
//                   you already know the buyer is qualified.
//   3. Brand review — magic-link signups awaiting approval (legacy path,
//      now uncommon since self-serve brand signup was removed).
//
// Inquiry workflow: submitted → contacted → qualified → account_created.
// "Create account" is only enabled at qualified — keeps the BD gate honest.
// Invite workflow: generate URL → paste in DM → recipient clicks →
// auto-provisioned + mirrored to orbis_prospects (Op CRM).

import { useEffect, useState } from 'react';
import { adminApi, inquiryService, type BrandInquiry, type BrandInvite } from '../lib/api';
import { PageHeader } from '../components/Page';
import { T } from '../theme';

type Tab = 'inquiries' | 'invites' | 'review';

interface QueueRow {
  contact_id: string;
  email: string;
  status: string;
  requested_at?: string;
  reviewed_at?: string;
  notes?: string;
}

const BUDGET_LABEL: Record<string, string> = {
  under_5k: 'Under $5k',
  '5k_to_25k': '$5k – $25k',
  '25k_to_100k': '$25k – $100k',
  '100k_plus': '$100k+',
  lets_discuss: "Let's discuss",
};

const STATUS_COLOR: Record<BrandInquiry['status'], string> = {
  submitted: T.warning,
  contacted: T.accent,
  qualified: T.live,
  unqualified: T.inkMute,
  account_created: T.live,
};

export default function Brands() {
  const [tab, setTab] = useState<Tab>('inquiries');

  // Tab counts kept in parent so the tab bar shows fresh numbers regardless
  // of which tab is active.
  const [inqCount, setInqCount] = useState<number>(0);
  const [openInviteCount, setOpenInviteCount] = useState<number>(0);
  const [reviewCount, setReviewCount] = useState<number>(0);

  useEffect(() => {
    adminApi.inquiries('open').then((d) => setInqCount(d.inquiries.length)).catch(() => {});
    adminApi.brandReviewQueue().then((d) => setReviewCount(d.pending.length)).catch(() => {});
    adminApi.listInvites()
      .then((d) => setOpenInviteCount(d.invites.filter((i) => !i.consumed_at).length))
      .catch(() => {});
  }, []);

  return (
    <>
      <PageHeader
        title="Brands"
        subtitle="Inquiries from /inquire, warm-lead invite links, and legacy magic-link signups."
      />

      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: `1px solid ${T.rule}` }}>
        <TabButton active={tab === 'inquiries'} onClick={() => setTab('inquiries')}>
          Inquiries {inqCount > 0 && <Badge n={inqCount} />}
        </TabButton>
        <TabButton active={tab === 'invites'} onClick={() => setTab('invites')}>
          Invites {openInviteCount > 0 && <Badge n={openInviteCount} />}
        </TabButton>
        <TabButton active={tab === 'review'} onClick={() => setTab('review')}>
          Brand review {reviewCount > 0 && <Badge n={reviewCount} />}
        </TabButton>
      </div>

      {tab === 'inquiries' && <InquiriesPanel onCount={setInqCount} />}
      {tab === 'invites' && <InvitesPanel onOpenCount={setOpenInviteCount} />}
      {tab === 'review' && <ReviewPanel onCount={setReviewCount} />}
    </>
  );
}

// ── Invites panel (warm-lead links) ────────────────────────────────────────

function InvitesPanel({ onOpenCount }: { onOpenCount: (n: number) => void }) {
  const [rows, setRows] = useState<BrandInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [company, setCompany] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [justGenerated, setJustGenerated] = useState<{ url: string; expires_at: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const load = () => {
    setLoading(true);
    adminApi
      .listInvites()
      .then((d) => {
        setRows(d.invites);
        onOpenCount(d.invites.filter((i) => !i.consumed_at).length);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  async function generate() {
    if (!company.trim()) {
      setError('Company name required.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const res = await adminApi.generateInvite({
        company_name: company.trim(),
        email_hint: emailHint.trim() || undefined,
        label: label.trim() || undefined,
      });
      setJustGenerated({ url: res.url, expires_at: res.expires_at });
      setCompany('');
      setEmailHint('');
      setLabel('');
      load();
    } catch (e) {
      setError(String(e));
    } finally {
      setCreating(false);
    }
  }

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }

  return (
    <>
      {error && (
        <div style={{ padding: 12, color: T.error, fontSize: 13, background: T.bgCard, borderRadius: 6, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div
        style={{
          padding: 18,
          background: T.bgCard,
          border: `1px solid ${T.rule}`,
          borderRadius: 8,
          marginBottom: 24,
        }}
      >
        <h3 style={{ fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.inkSoft, marginBottom: 14 }}>
          Generate invite link
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 10, alignItems: 'end' }}>
          <div>
            <label style={miniLabelStyle}>Company *</label>
            <input
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Acme Records"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={miniLabelStyle}>Email hint (optional)</label>
            <input
              value={emailHint}
              onChange={(e) => setEmailHint(e.target.value)}
              placeholder="jamie@acme.com"
              type="email"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={miniLabelStyle}>Internal label (optional)</label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="LinkedIn intro from Jane"
              style={inputStyle}
            />
          </div>
          <ActionButton primary onClick={generate} disabled={creating} busy={creating}>
            Generate
          </ActionButton>
        </div>
        {justGenerated && (
          <div
            style={{
              marginTop: 14,
              padding: 12,
              background: T.bgSoft,
              border: `1px solid ${T.live}`,
              borderRadius: 6,
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <code style={{ flex: 1, fontSize: 12, color: T.ink, fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {justGenerated.url}
            </code>
            <button
              onClick={() => copy(justGenerated.url)}
              style={{
                padding: '6px 12px',
                fontSize: 11,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                background: T.live,
                color: T.bg,
                border: 'none',
                borderRadius: 4,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {copied ? 'Copied' : 'Copy URL'}
            </button>
            <span style={{ fontSize: 11, color: T.inkMute }}>
              Expires {justGenerated.expires_at.slice(0, 10)}
            </span>
          </div>
        )}
      </div>

      <h2 style={{ fontSize: 12, letterSpacing: '0.32em', textTransform: 'uppercase', color: T.inkSoft, marginBottom: 12 }}>
        Recent invites ({rows.length})
      </h2>

      {loading ? (
        <div style={{ padding: 24, color: T.inkMute, fontSize: 13 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 32, background: T.bgCard, border: `1px dashed ${T.rule}`, borderRadius: 8, textAlign: 'center', color: T.inkMute, fontStyle: 'normal' }}>
          No invites yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map((r) => (
            <InviteRow key={r.id} row={r} />
          ))}
        </div>
      )}
    </>
  );
}

function InviteRow({ row }: { row: BrandInvite }) {
  const consumed = !!row.consumed_at;
  const expired = !consumed && new Date(row.expires_at) < new Date();
  const status = consumed ? 'consumed' : expired ? 'expired' : 'open';
  const color = consumed ? T.live : expired ? T.error : T.warning;

  return (
    <div
      style={{
        padding: 12,
        background: T.bgCard,
        border: `1px solid ${T.rule}`,
        borderRadius: 6,
        display: 'grid',
        gridTemplateColumns: '2fr 1.5fr 1fr 1fr',
        gap: 12,
        alignItems: 'center',
        fontSize: 13,
      }}
    >
      <div>
        <div style={{ color: T.ink, fontWeight: 500 }}>{row.company_name_hint}</div>
        {row.label && <div style={{ fontSize: 11, color: T.inkMute, marginTop: 2 }}>{row.label}</div>}
      </div>
      <div style={{ fontSize: 12, color: T.inkSoft }}>
        {row.consumed_email ?? row.email_hint ?? '—'}
      </div>
      <span
        style={{
          fontSize: 10,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color,
          fontWeight: 700,
        }}
      >
        {status}
      </span>
      <span style={{ fontSize: 11, color: T.inkMute }}>
        {consumed ? `Used ${row.consumed_at?.slice(0, 10)}` : `Expires ${row.expires_at.slice(0, 10)}`}
      </span>
    </div>
  );
}

const miniLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 10,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: T.inkMute,
  marginBottom: 4,
};

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '10px 16px',
        marginBottom: -1,
        background: 'transparent',
        color: active ? T.ink : T.inkMute,
        border: 'none',
        borderBottom: `2px solid ${active ? T.accent : 'transparent'}`,
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      {children}
    </button>
  );
}

function Badge({ n }: { n: number }) {
  return (
    <span
      style={{
        background: T.accent,
        color: T.bg,
        fontSize: 10,
        fontWeight: 700,
        padding: '2px 7px',
        borderRadius: 999,
        letterSpacing: '0.02em',
      }}
    >
      {n}
    </span>
  );
}

// ── Inquiries panel ────────────────────────────────────────────────────────

function InquiriesPanel({ onCount }: { onCount: (n: number) => void }) {
  const [status, setStatus] = useState<'open' | 'all' | BrandInquiry['status']>('open');
  const [svc, setSvc] = useState<'all' | 'localization' | 'clipping'>('all');
  const [rows, setRows] = useState<BrandInquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    adminApi
      .inquiries(status)
      .then((d) => {
        setRows(d.inquiries);
        // Always refresh the "open" count in the tab even when filtering elsewhere.
        if (status === 'open') onCount(d.inquiries.length);
        else adminApi.inquiries('open').then((o) => onCount(o.inquiries.length)).catch(() => {});
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(load, [status]);

  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        {(['open', 'submitted', 'contacted', 'qualified', 'unqualified', 'account_created', 'all'] as const).map((s) => (
          <FilterPill key={s} active={status === s} onClick={() => setStatus(s)}>
            {s.replace('_', ' ')}
          </FilterPill>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {(['all', 'localization', 'clipping'] as const).map((s) => (
          <FilterPill key={s} active={svc === s} onClick={() => setSvc(s)}>
            {s === 'all' ? 'All services' : s}
          </FilterPill>
        ))}
      </div>

      {error && (
        <div style={{ padding: 12, color: T.error, fontSize: 13, background: T.bgCard, borderRadius: 6, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 24, color: T.inkMute, fontSize: 13 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 32, background: T.bgCard, border: `1px dashed ${T.rule}`, borderRadius: 8, textAlign: 'center', color: T.inkMute, fontStyle: 'normal' }}>
          Nothing here.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.filter((r) => svc === 'all' || inquiryService(r) === svc).map((r) => (
            <InquiryRow
              key={r.id}
              row={r}
              expanded={expanded === r.id}
              onToggle={() => setExpanded(expanded === r.id ? null : r.id)}
              onChanged={load}
            />
          ))}
        </div>
      )}
    </>
  );
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 12px',
        background: active ? T.accent : 'transparent',
        color: active ? T.bg : T.inkSoft,
        border: `1px solid ${active ? T.accent : T.rule}`,
        borderRadius: 999,
        fontSize: 12,
        cursor: 'pointer',
        fontWeight: 500,
        textTransform: 'capitalize',
      }}
    >
      {children}
    </button>
  );
}

function InquiryRow({
  row,
  expanded,
  onToggle,
  onChanged,
}: {
  row: BrandInquiry;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => void;
}) {
  const [notes, setNotes] = useState(row.operator_notes ?? '');
  const [unqualReason, setUnqualReason] = useState('');
  const [brandName, setBrandName] = useState(row.company_name);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [created, setCreated] = useState<{ link: string; email: string; emailed: boolean } | null>(null);
  const [copied, setCopied] = useState(false);

  async function patch(
    p: {
      status?: 'contacted' | 'qualified' | 'unqualified';
      operator_notes?: string;
      unqualified_reason?: string;
      mark_contacted?: boolean;
    },
    busyTag: string
  ) {
    setBusy(busyTag);
    setErr(null);
    try {
      await adminApi.updateInquiry(row.id, p);
      onChanged();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(null);
    }
  }

  async function createAccount() {
    if (
      !confirm(
        `Create the Dreamlink Studio account for ${brandName}?\n\nThis provisions their brand, approves them, and generates a login link for ${row.contact_email} (also emailed to them). You'll get the link to copy + send.`
      )
    )
      return;
    setBusy('create');
    setErr(null);
    try {
      const res = await adminApi.createAccountFromInquiry(row.id, brandName);
      setCreated({ link: res.login_link, email: res.email, emailed: res.emailed });
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(null);
    }
  }

  async function copyLink() {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — operator can select the text manually */
    }
  }

  return (
    <div
      style={{
        background: T.bgCard,
        border: `1px solid ${T.rule}`,
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          padding: 14,
          background: 'transparent',
          border: 'none',
          textAlign: 'left',
          display: 'grid',
          gridTemplateColumns: '2fr 1.4fr 1fr 0.8fr auto',
          gap: 12,
          alignItems: 'center',
          cursor: 'pointer',
          color: T.ink,
          fontFamily: 'inherit',
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {row.company_name}
            {inquiryService(row) === 'localization' && (
              <span style={{ fontSize: 10, fontWeight: 600, color: T.accent, border: `1px solid ${T.rule}`, borderRadius: 999, padding: '2px 8px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Localization
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: T.inkMute, marginTop: 2 }}>{row.contact_email}</div>
        </div>
        <div style={{ fontSize: 12, color: T.inkSoft }}>{row.contact_name}</div>
        <div style={{ fontSize: 12, color: T.inkSoft }}>{BUDGET_LABEL[row.budget_range] ?? row.budget_range}</div>
        <span
          style={{
            fontSize: 10,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: STATUS_COLOR[row.status],
            fontWeight: 700,
          }}
        >
          {row.status.replace('_', ' ')}
        </span>
        <span style={{ fontSize: 11, color: T.inkMute }}>
          {row.inquired_at.slice(0, 10)}
        </span>
      </button>

      {expanded && (
        <div style={{ padding: 16, borderTop: `1px solid ${T.rule}`, background: T.bgSoft }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 14 }}>
            <KV k="Company" v={row.company_name} />
            <KV k="Website / social" v={row.website_or_social} />
            <KV k="Contact" v={`${row.contact_name} <${row.contact_email}>`} />
            <KV k="Budget" v={BUDGET_LABEL[row.budget_range] ?? row.budget_range} />
            {row.utm_source && <KV k="utm_source" v={row.utm_source} />}
            {row.utm_campaign && <KV k="utm_campaign" v={row.utm_campaign} />}
          </div>

          <KV k="What they want" v={row.what_they_want} block />

          {inquiryService(row) === 'localization' && (
            <QualPanel inquiryId={row.id} status={row.status} />
          )}

          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Operator notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={() => {
                if (notes !== (row.operator_notes ?? '')) {
                  void patch({ operator_notes: notes }, 'notes');
                }
              }}
              rows={3}
              placeholder="Internal notes — call summary, next step, follow-up date…"
              style={textareaStyle}
            />
          </div>

          {err && (
            <div style={{ marginTop: 10, color: T.error, fontSize: 12 }}>{err}</div>
          )}

          {/* Action row depends on status. */}
          <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {row.status === 'submitted' && (
              <ActionButton
                onClick={() => patch({ mark_contacted: true }, 'contact')}
                disabled={busy !== null}
                busy={busy === 'contact'}
              >
                Mark contacted
              </ActionButton>
            )}
            {row.status === 'contacted' && (
              <ActionButton
                onClick={() => patch({ status: 'qualified' }, 'qual')}
                disabled={busy !== null}
                busy={busy === 'qual'}
              >
                Mark qualified
              </ActionButton>
            )}
            {row.status === 'qualified' && (
              <>
                <input
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="Brand name (defaults to company)"
                  style={{ ...inputStyle, maxWidth: 260 }}
                />
                <ActionButton
                  primary
                  onClick={createAccount}
                  disabled={busy !== null}
                  busy={busy === 'create'}
                >
                  Create account + send invite
                </ActionButton>
              </>
            )}
            {row.status === 'account_created' && (
              <span style={{ fontSize: 12, color: T.live }}>
                ✓ Account created {row.account_created_at?.slice(0, 16)} · brand {row.brand_id}
              </span>
            )}
            {row.status !== 'unqualified' && row.status !== 'account_created' && (
              <details style={{ marginLeft: 'auto' }}>
                <summary style={{ fontSize: 12, color: T.inkMute, cursor: 'pointer' }}>Unqualify</summary>
                <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                  <input
                    placeholder="Reason (optional)"
                    value={unqualReason}
                    onChange={(e) => setUnqualReason(e.target.value)}
                    style={{ ...inputStyle, width: 220 }}
                  />
                  <ActionButton
                    danger
                    onClick={() => patch({ status: 'unqualified', unqualified_reason: unqualReason }, 'unqual')}
                    disabled={busy !== null}
                    busy={busy === 'unqual'}
                  >
                    Confirm
                  </ActionButton>
                </div>
              </details>
            )}
            {row.status === 'unqualified' && row.unqualified_reason && (
              <span style={{ fontSize: 12, color: T.inkMute, fontStyle: 'normal' }}>
                Unqualified: {row.unqualified_reason}
              </span>
            )}
          </div>

          {err && (
            <div style={{ marginTop: 12, fontSize: 12, color: T.error }}>{err}</div>
          )}

          {created && (
            <div
              style={{
                marginTop: 14,
                padding: 16,
                background: T.bg,
                border: `1px solid ${T.live}`,
                borderRadius: 10,
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 600, color: T.ink, marginBottom: 4 }}>
                ✓ Account created for {brandName}
              </div>
              <div style={{ fontSize: 12.5, color: T.inkSoft, marginBottom: 12 }}>
                {created.emailed
                  ? `A login link was emailed to ${created.email}.`
                  : `Email wasn't sent — copy the link below and send it to ${created.email} yourself.`}{' '}
                You can also copy it here and send it directly (Slack, WhatsApp, etc.). Valid 7 days.
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  readOnly
                  value={created.link}
                  onFocus={(e) => e.currentTarget.select()}
                  style={{ ...inputStyle, flex: 1, minWidth: 240, fontFamily: T.font.mono, fontSize: 12 }}
                />
                <ActionButton onClick={copyLink} disabled={false}>
                  {copied ? 'Copied' : 'Copy link'}
                </ActionButton>
                <ActionButton
                  onClick={() => {
                    setCreated(null);
                    onChanged();
                  }}
                  disabled={false}
                >
                  Done
                </ActionButton>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KV({ k, v, block }: { k: string; v: string; block?: boolean }) {
  return (
    <div style={block ? { gridColumn: '1 / -1' } : undefined}>
      <div style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.inkMute, marginBottom: 4 }}>
        {k}
      </div>
      <div style={{ fontSize: 13, color: T.ink, whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{v}</div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 10,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: T.inkMute,
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  background: T.bgCard,
  color: T.ink,
  border: `1px solid ${T.rule}`,
  padding: '7px 10px',
  borderRadius: 4,
  fontSize: 13,
  fontFamily: 'inherit',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  width: '100%',
  resize: 'vertical',
  lineHeight: 1.5,
};

// ── Localization qualification panel (lazy-loaded inside InquiryRow) ─────────

function QualPanel({
  inquiryId,
  status,
}: {
  inquiryId: string;
  status: BrandInquiry['status'];
}) {
  const [q, setQ] = useState<{
    qual: Record<string, unknown>;
    assets: { id: string; content_type: string; size_bytes: number; created_at: string }[];
    analytics_unverified: boolean;
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = () => {
    adminApi.inquiryQual(inquiryId).then(setQ).catch((e: unknown) => setErr(String(e)));
  };
  useEffect(() => { load(); }, [inquiryId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) {
    return (
      <div style={{ fontSize: 12, color: T.inkMute, marginTop: 10 }}>
        No qualification on file yet.
      </div>
    );
  }
  if (!q) {
    return (
      <div style={{ fontSize: 12, color: T.inkMute, marginTop: 10 }}>
        Loading qualification...
      </div>
    );
  }

  const state = String(q.qual.state ?? '');
  const completeness = Number(q.qual.completeness ?? 0);

  async function route() {
    if (
      !confirm(
        'Route this packet to the localization partner now? This sends the brief and any screenshots.'
      )
    )
      return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await adminApi.routeToPartner(inquiryId);
      setMsg(`Routed at ${r.routed_at.slice(0, 16)}`);
      load();
    } catch (e: unknown) {
      const s = String(e);
      // Verification contract: each HTTP status maps to an honest, specific message.
      // Never show "Routed" unless the 200 came back.
      setMsg(
        s.includes('503')
          ? 'Partner intake address is not configured on this deployment yet. Nothing was sent.'
          : s.includes('409')
          ? 'Not routed — packet is not yet ready, or was already routed.'
          : s.includes('502')
          ? 'The partner email failed to send. Nothing was marked routed. Please try again.'
          : 'Failed to route. Please try again.'
      );
    } finally {
      setBusy(false);
    }
  }

  async function markPartnerResponded() {
    try {
      await adminApi.patchQual(inquiryId, 'partner_responded');
      load();
    } catch (e: unknown) {
      setMsg(String(e));
    }
  }

  return (
    <div
      style={{
        marginTop: 12,
        padding: 12,
        background: T.bgCard,
        borderRadius: 8,
        border: `1px solid ${T.rule}`,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: T.inkSoft }}>
        Localization qualification
      </div>
      <div style={{ fontSize: 13, marginTop: 6 }}>
        State: <b>{state || 'not started'}</b> {' '} {completeness} of 4 ready
        {q.analytics_unverified && (
          <span style={{ color: T.error, marginLeft: 8, fontSize: 12 }}>
            analytics unverified (client-asserted access — confirm before relying on it)
          </span>
        )}
      </div>

      {q.assets.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12 }}>
          {q.assets.map((a, i) => (
            <a
              key={a.id}
              href={adminApi.qualAssetUrl(a.id)}
              target="_blank"
              rel="noreferrer"
              style={{ marginRight: 10, color: T.accent }}
            >
              screenshot {i + 1}
            </a>
          ))}
        </div>
      )}

      <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {state === 'packet_ready' && (
          <ActionButton primary onClick={route} disabled={busy} busy={busy}>
            Route to partner
          </ActionButton>
        )}
        {state === 'routed_to_partner' && (
          <ActionButton onClick={markPartnerResponded} disabled={busy} busy={busy}>
            Mark partner responded
          </ActionButton>
        )}
        {status !== 'unqualified' && state !== 'routed_to_partner' && state !== 'partner_responded' && (
          <ActionButton
            danger
            onClick={async () => {
              try { await adminApi.patchQual(inquiryId, 'unqualified'); load(); }
              catch (e: unknown) { setMsg(String(e)); }
            }}
            disabled={busy}
          >
            Mark unqualified
          </ActionButton>
        )}
        {state !== 'on_hold' && state !== 'routed_to_partner' && state !== 'partner_responded' && (
          <ActionButton
            onClick={async () => {
              try { await adminApi.patchQual(inquiryId, 'on_hold'); load(); }
              catch (e: unknown) { setMsg(String(e)); }
            }}
            disabled={busy}
          >
            Hold
          </ActionButton>
        )}
      </div>

      {msg && (
        <div style={{ marginTop: 8, fontSize: 12, color: T.inkSoft }}>{msg}</div>
      )}
    </div>
  );
}

function ActionButton({
  children,
  onClick,
  disabled,
  busy,
  primary,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  primary?: boolean;
  danger?: boolean;
}) {
  const bg = danger ? 'transparent' : primary ? T.live : T.bgCard;
  const color = danger ? T.error : primary ? T.bg : T.ink;
  const border = danger ? `1px solid ${T.error}` : `1px solid ${primary ? T.live : T.rule}`;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '8px 14px',
        fontSize: 12,
        letterSpacing: '0.06em',
        background: bg,
        color,
        border,
        borderRadius: 4,
        fontWeight: 600,
        cursor: disabled ? 'wait' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        fontFamily: 'inherit',
      }}
    >
      {busy ? 'Working…' : children}
    </button>
  );
}

// ── Brand review panel (legacy magic-link signups) ─────────────────────────

function ReviewPanel({ onCount }: { onCount: (n: number) => void }) {
  const [pending, setPending] = useState<QueueRow[]>([]);
  const [recent, setRecent] = useState<QueueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draftName, setDraftName] = useState<Record<string, string>>({});

  const load = () => {
    setLoading(true);
    adminApi
      .brandReviewQueue()
      .then((d) => {
        setPending(d.pending);
        setRecent(d.recent);
        onCount(d.pending.length);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  async function approve(row: QueueRow) {
    setBusy(row.email);
    try {
      await adminApi.approveBrand(row.email, draftName[row.email]);
      load();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(null);
    }
  }

  async function reject(row: QueueRow) {
    if (!confirm(`Reject ${row.email}?`)) return;
    setBusy(row.email);
    try {
      await adminApi.rejectBrand(row.email);
      load();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      {error && (
        <div style={{ padding: 12, color: T.error, fontSize: 13, background: T.bgCard, borderRadius: 6, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <section style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 12, letterSpacing: '0.32em', textTransform: 'uppercase', color: T.inkSoft, marginBottom: 12 }}>
          Pending ({pending.length})
        </h2>

        {pending.length === 0 && !loading && (
          <div style={{ padding: 32, background: T.bgCard, border: `1px dashed ${T.rule}`, borderRadius: 8, textAlign: 'center', color: T.inkMute, fontStyle: 'normal' }}>
            Nothing waiting for review.
          </div>
        )}

        {pending.map((p) => (
          <div
            key={p.contact_id}
            style={{
              padding: 16,
              background: T.bgCard,
              border: `1px solid ${T.warning}`,
              borderRadius: 8,
              marginBottom: 8,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr auto auto',
              gap: 12,
              alignItems: 'center',
            }}
          >
            <div>
              <div style={{ fontSize: 14, color: T.ink, fontWeight: 500 }}>{p.email}</div>
              <div style={{ fontSize: 11, color: T.inkMute, marginTop: 2 }}>
                Requested {p.requested_at ?? ''}
              </div>
            </div>
            <input
              placeholder="Brand display name (optional)"
              value={draftName[p.email] ?? ''}
              onChange={(e) => setDraftName({ ...draftName, [p.email]: e.target.value })}
              style={inputStyle}
            />
            <ActionButton primary onClick={() => approve(p)} disabled={busy === p.email} busy={busy === p.email}>
              Approve
            </ActionButton>
            <ActionButton danger onClick={() => reject(p)} disabled={busy === p.email} busy={busy === p.email}>
              Reject
            </ActionButton>
          </div>
        ))}
      </section>

      <section>
        <h2 style={{ fontSize: 12, letterSpacing: '0.32em', textTransform: 'uppercase', color: T.inkSoft, marginBottom: 12 }}>
          Recent decisions
        </h2>
        {recent.length === 0 && (
          <div style={{ padding: 24, color: T.inkMute, fontStyle: 'normal', fontSize: 13 }}>
            No recent decisions.
          </div>
        )}
        {recent.map((r) => (
          <div
            key={r.contact_id}
            style={{
              padding: 12,
              background: T.bgCard,
              borderRadius: 6,
              marginBottom: 6,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: 13,
            }}
          >
            <span style={{ color: T.inkSoft }}>{r.email}</span>
            <span
              style={{
                fontSize: 10,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: r.status === 'approved' ? T.live : T.error,
              }}
            >
              {r.status} · {r.reviewed_at?.slice(0, 16) ?? ''}
            </span>
          </div>
        ))}
      </section>
    </>
  );
}
